import { currentAutosaveScope, setAutosaveScope } from '@/core/api/autosaveScope';

import { apiUrl } from "./base";
import type { Me } from "./auth";

const ORGANISATION_HEADER = "X-Admination-Organisation";

function detailTextOf(detail: unknown): string {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "detail" in detail) {
    const inner = (detail as { detail: unknown }).detail;
    if (typeof inner === "string") return inner;
  }
  return "";
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    public readonly detail: unknown,

    public readonly retryAfter?: number
  ) {

    super(detailTextOf(detail) || `API ${status} on ${path}`);
    this.name = "ApiError";
  }

  get detailText(): string {
    return detailTextOf(this.detail);
  }
}

export class TransportError extends TypeError {
  name = "TransportError";
}

export class RequestTimeoutError extends TransportError {
  name = "RequestTimeoutError";
}

function throwTransportError(error: unknown): never {
  if (error instanceof SyntaxError) throw error;
  if (error instanceof DOMException && error.name === "TimeoutError") {
    throw new RequestTimeoutError("Der Server antwortet nicht. Bitte versuchen Sie es in einem Moment erneut.", { cause: error });
  }
  throw new TransportError(error instanceof Error ? error.message : "Network request failed", { cause: error });
}

const REQUEST_TIMEOUT_MS = 30_000;

const MIDDLE_TIMEOUT_MS = 180_000;

const LONG_TIMEOUT_MS = 600_000;

const WRITE_ROUTE_TIMEOUTS_MS: readonly (readonly [RegExp, number])[] = [
  [/^\/documents\/tb$/, LONG_TIMEOUT_MS],
  [/^\/geva-klett\/[^/]+\/geva$/, LONG_TIMEOUT_MS],
  [/^\/verbis-lebenslauf\/[^/]+\/(lebenslauf|dateien)$/, LONG_TIMEOUT_MS],
  [/^\/(kompetenzbilanzierung|erstbericht)\/[^/]+\/erzeugen$/, LONG_TIMEOUT_MS],
  [/^\/portal\/vergabe\/blatt\/[^/]+\/unterschrift$/, MIDDLE_TIMEOUT_MS],
  [/^\/bewertungsbogen\//, MIDDLE_TIMEOUT_MS],
  [/^\/storage\/documents$/, MIDDLE_TIMEOUT_MS],
  [/^\/storage\/signing-assets\//, MIDDLE_TIMEOUT_MS],
  [/^\/documents\/anwesenheitsliste$/, MIDDLE_TIMEOUT_MS],
  [/\/gegenzeichnung$/, MIDDLE_TIMEOUT_MS],
  [/^\/geva-klett\/[^/]+\/klett$/, MIDDLE_TIMEOUT_MS],
  [/^\/verbis-lebenslauf\/[^/]+\/verbis$/, MIDDLE_TIMEOUT_MS],
  [/^\/extraction\//, MIDDLE_TIMEOUT_MS],
  [/^\/auth\/login$/, MIDDLE_TIMEOUT_MS],
  [/^\/portal\/sign-anwesenheitsliste$/, MIDDLE_TIMEOUT_MS],
];

const STALL_MS = 5_000;

let restartLive: (() => boolean) | null = null;
let lastLiveRestart = 0;

export function setLiveRestart(restart: (() => boolean) | null): void {
  restartLive = restart;
}

function watchForStall(): () => void {
  let timer: ReturnType<typeof setTimeout>;
  const check = () => {
    const wait = lastLiveRestart + STALL_MS - Date.now();
    if (wait <= 0 && restartLive?.()) lastLiveRestart = Date.now();
    timer = setTimeout(check, wait > 0 ? wait : STALL_MS);
  };
  timer = setTimeout(check, STALL_MS);
  return () => clearTimeout(timer);
}

export function apiFetch(url: string, init: RequestInit, timeoutMs = REQUEST_TIMEOUT_MS): Promise<Response> {
  const response = fetch(url, { ...init, signal: AbortSignal.timeout(timeoutMs) }).catch(throwTransportError);
  if (timeoutMs !== REQUEST_TIMEOUT_MS) return response;
  return response.finally(watchForStall());
}

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];

export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = apiUrl(path);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const item of value) url.searchParams.append(key, String(item));
      continue;
    }
    url.searchParams.set(key, String(value as string | number | boolean));
  }
  return url.toString();
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

let authenticatedUser: string | null = null;
let authenticationGeneration = 0;
let authenticationStarted = 0;
let authenticationApplied = 0;

type AuthenticationUpdate = Readonly<{ generation: number; id: number; kind: "probe" | "session" | "variant" }>;

function invalidateAuthenticationUpdates(): void {
  authenticationGeneration += 1;
}

export function beginAuthenticationUpdate(kind: AuthenticationUpdate["kind"] = "probe"): AuthenticationUpdate {
  const id = ++authenticationStarted;
  if (kind !== "probe") invalidateAuthenticationUpdates();
  return { generation: authenticationGeneration, id, kind };
}

function authenticationUpdateCurrent(update: AuthenticationUpdate): boolean {
  return update.generation === authenticationGeneration && (update.kind === "session" || update.id >= authenticationApplied);
}

export function publishAuthentication(update: AuthenticationUpdate, apply: () => void): void {
  if (!authenticationUpdateCurrent(update)) throw new Error("Die Sitzungsabfrage wurde durch eine neuere Sitzung ersetzt.");
  if (update.kind !== "probe") invalidateAuthenticationUpdates();
  authenticationApplied = update.id;
  apply();
}

export function setAuthenticatedUser(userId: string | null): void {
  if (authenticatedUser !== userId) setAutosaveScope(null);
  authenticatedUser = userId;
}

const csrfRefreshes = new Map<Promise<unknown>, AuthenticationUpdate>();

export function trackCsrfRefresh<T>(work: Promise<T>, update = beginAuthenticationUpdate()): Promise<T> {
  csrfRefreshes.set(work, update);
  const settle = () => {
    csrfRefreshes.delete(work);
  };
  work.then(settle, () => { if (authenticationUpdateCurrent(update)) csrfToken = null; settle(); });
  return work;
}

async function csrfRefreshesSettled(): Promise<void> {

  while (csrfRefreshes.size > 0) await Promise.all([...csrfRefreshes].map(async ([work, update]) => {
    try { await work; } catch (error) { if (authenticationUpdateCurrent(update)) throw error; }
  }));
}

let activeOrganisationId: string | null = null;

export function setActiveOrganisationId(organisationId: string | null): void {
  if (activeOrganisationId !== organisationId) invalidateAuthenticationUpdates();
  activeOrganisationId = organisationId;
}

export function currentActiveOrganisationId(): string | null {
  return activeOrganisationId;
}

export function setAuthenticatedSession(session: Pick<Me, "user_id" | "csrf_token" | "organisations">, update: AuthenticationUpdate): void {
  publishAuthentication(update, () => {
    setAuthenticatedUser(session.user_id);
    const scope = currentAutosaveScope();
    if (scope !== null) {
      const membership = session.organisations.find((entry) => entry.organisation_id === activeOrganisationId);
      if (scope !== JSON.stringify([session.user_id, activeOrganisationId, membership?.effective_variant])) {
        setAutosaveScope(null);
      }
    }
    setCsrfToken(session.csrf_token ?? null);
  });
}

function baseHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (activeOrganisationId) headers[ORGANISATION_HEADER] = activeOrganisationId;
  return headers;
}

function readDetail(response: Response): Promise<unknown> {
  return response.json().catch(() => undefined);
}

const RETRY_AFTER_MAX_SECONDS = 2147483647;

export function parseRetryAfter(value: string | null): number | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number(trimmed);
    if (!Number.isFinite(seconds) || seconds > RETRY_AFTER_MAX_SECONDS) return undefined;
    return seconds;
  }
  return undefined;
}

async function toApiError(response: Response, path: string): Promise<ApiError> {
  const detail = await readDetail(response);
  const retryAfter =
    response.status === 429 ? parseRetryAfter(response.headers.get("Retry-After")) : undefined;
  return new ApiError(response.status, path, detail, retryAfter);
}

export async function getJson<T>(
  path: string,
  query?: Record<string, QueryValue>,
  onHeaders?: (headers: Headers) => void,
): Promise<T> {
  const response = await apiFetch(buildUrl(path, query), {
    method: "GET",
    credentials: "include",
    headers: baseHeaders(),
  });
  if (!response.ok) throw await toApiError(response, path);
  onHeaders?.(response.headers);
  return (await response.json().catch(throwTransportError)) as T;
}

export async function getBlob(path: string): Promise<Blob> {
  const response = await apiFetch(buildUrl(path), {
    method: "GET",
    credentials: "include",
    headers: { ...baseHeaders(), Accept: "*/*" },
  });
  if (!response.ok) throw await toApiError(response, path);
  return await response.blob().catch(throwTransportError);
}

export async function getJsonOrNull<T>(
  path: string,
  query?: Record<string, QueryValue>
): Promise<T | null> {
  try {
    return await getJson<T>(path, query);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export async function rotateCsrfToken(): Promise<string | null> {

  await csrfRefreshesSettled();
  const update = beginAuthenticationUpdate();
  return trackCsrfRefresh(
    (async () => {
      try {
        const response = await apiFetch(buildUrl("/auth/csrf"), {
          method: "POST",
          credentials: "include",
          headers: baseHeaders(),
        });
        if (!response.ok) throw await toApiError(response, "/auth/csrf");
        const body = (await response.json().catch(throwTransportError)) as { csrf_token?: string | null };
        if (!authenticationUpdateCurrent(update)) return csrfToken;
        if (authenticatedUser !== null) {
          const session = await getJson<Me>("/auth/me");
          setAuthenticatedSession(session, update);
        } else publishAuthentication(update, () => { csrfToken = body.csrf_token ?? null; });
        return csrfToken;
      } catch (error) {
        if (!authenticationUpdateCurrent(update)) return csrfToken;
        throw error;
      }
    })(), update
  );
}

type SendInit = {
  method: "POST" | "PATCH" | "PUT" | "DELETE";
  body?: BodyInit;
  contentType?: string;

  accept?: string;
  identity?: WriteIdentity;
};

function writeTimeoutMs(path: string, init: SendInit): number {
  if (init.method === "DELETE") return REQUEST_TIMEOUT_MS;
  const route = WRITE_ROUTE_TIMEOUTS_MS.find(([pattern]) => pattern.test(path));
  if (route) return route[1];
  return init.body instanceof FormData ? MIDDLE_TIMEOUT_MS : REQUEST_TIMEOUT_MS;
}

export type WriteIdentity = Readonly<{ scope: string | null; userId: string | null; headers: Readonly<Record<string, string>> }>;

export function captureWriteIdentity(): WriteIdentity {
  return { scope: currentAutosaveScope(), userId: authenticatedUser, headers: baseHeaders() };
}

export function isWriteIdentityCurrent(identity: WriteIdentity): boolean {
  const headers = baseHeaders();
  return currentAutosaveScope() === identity.scope && authenticatedUser === identity.userId && headers[ORGANISATION_HEADER] === identity.headers[ORGANISATION_HEADER];
}

export function assertWriteIdentity(identity: WriteIdentity): void {
  if (!isWriteIdentityCurrent(identity)) {
    throw new Error("Der aktive Arbeitsbereich hat sich geändert. Bitte öffnen Sie das Formular erneut.");
  }
}

async function sendOnce(path: string, query: Record<string, QueryValue> | undefined, init: SendInit, identity: WriteIdentity) {
  await csrfRefreshesSettled();
  assertWriteIdentity(identity);
  const headers = { ...identity.headers };
  if (init.accept) headers["Accept"] = init.accept;
  if (init.contentType) headers["Content-Type"] = init.contentType;
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  return apiFetch(buildUrl(path, query), {
    method: init.method,
    credentials: "include",
    headers,
    body: init.body,
  }, writeTimeoutMs(path, init));
}

async function sendWithRetry(
  path: string,
  query: Record<string, QueryValue> | undefined,
  init: SendInit,
  identity: WriteIdentity,
): Promise<Response> {
  let response = await sendOnce(path, query, init, identity);
  if (response.status === 403) {
    const detail = await readDetail(response.clone());
    const text = new ApiError(403, path, detail).detailText;
    if (text.includes("csrf")) {
      assertWriteIdentity(identity);
      await rotateCsrfToken();
      response = await sendOnce(path, query, init, identity);
    }
  }
  assertWriteIdentity(identity);
  return response;
}

export async function send<T>(
  path: string,
  init: SendInit,
  query?: Record<string, QueryValue>
): Promise<T> {
  const identity = init.identity ?? captureWriteIdentity();
  try {
    const response = await sendWithRetry(path, query, init, identity);
    if (!response.ok) throw await toApiError(response, path);
    if (response.status === 204 || response.headers.get("content-length") === "0") {
      return undefined as T;
    }
    const text = await response.text().catch(throwTransportError);
    return (text ? JSON.parse(text) : undefined) as T;
  } finally {
    assertWriteIdentity(identity);
  }
}

export async function postForBlob(
  path: string,
  body: unknown,
  accept: string,
  query?: Record<string, QueryValue>,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<Blob> {
  try {
    const response = await sendWithRetry(path, query, {
      method: "POST",
      body: JSON.stringify(body),
      contentType: "application/json",
      accept,
    }, identity);
    if (!response.ok) throw new ApiError(response.status, path, await readDetail(response));
    return await response.blob().catch(throwTransportError);
  } finally {
    assertWriteIdentity(identity);
  }
}

export function sendJson<T>(
  path: string,
  method: "POST" | "PATCH" | "PUT",
  body: unknown,
  query?: Record<string, QueryValue>,
  identity?: WriteIdentity,
): Promise<T> {
  return send<T>(
    path,
    { method, body: JSON.stringify(body), contentType: "application/json", identity },
    query
  );
}

export function sendDelete(path: string, query?: Record<string, QueryValue>, identity?: WriteIdentity): Promise<void> {
  return send<void>(path, { method: "DELETE", identity }, query);
}

export function sendForm<T>(
  path: string,
  form: FormData,
  method: "POST" | "PUT" = "POST",
  identity?: WriteIdentity,
): Promise<T> {
  return send<T>(path, { method, body: form, identity });
}

export function fillPath(template: string, params: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined) throw new Error(`missing path parameter ${key} for ${template}`);
    return encodeURIComponent(value);
  });
}
